import { Dispatch, SetStateAction, useState } from "react";
import { FoodsIcon, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { IngredientEcsBreakdown } from "src/api/client-api/products/useProductLivePreview";
import type { ProductLivePreview } from "src/api/client-api/products/useProductLivePreview";
import { AddItemField } from "src/components/EntityForm/AddItemField";
import { ProductFormProductIngredient, ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { IngredientsSearchModalV2 } from "src/pages/Product/v2/ProductFormV2/Recipe/IngredientsSearchModalV2";
import { IngredientCardV2 } from "src/pages/Product/v2/ProductFormV2/Recipe/IngredientCardV2";
import { getProductIngredientTransportKey } from "./productIngredientTransportKey";
import { getIngredientLineScoreKey, getIngredientTransportLineScoreKey } from "../sectionScores";
import {
    computeEdibleMassG,
    type EcobalyseIngredientMassRatiosMap,
    resolveEdibleMassG,
    resolveIngredientMassRatios,
} from "../ecobalyseMass";
import { useIngredientMassRatios } from "../useIngredientMassRatios";
import { resolveSelectedIngredientOrigin, useHydrateIngredientOrigins } from "../Recipe/useHydrateIngredientOrigins";
import type { TransportMaterializationPendingChange } from "./ProductTransportInlineSelector";
import type { FieldError } from "../validation";
import { getRecipeWeightValidation } from "../recipeWeightValidation";

type Step4RecipeProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    disabled?: boolean;
    errors?: FieldError[];
    lineScores?: Record<string, string | null>;
    transportLineScores?: Record<string, string | null>;
    ingredientEcsBreakdown?: Record<string, IngredientEcsBreakdown>;
    resolvedTransports?: ProductLivePreview["resolvedTransports"];
    scoreUnit?: string;
    onTransportMaterializationPendingChange?: TransportMaterializationPendingChange;
};

const formatWeightForDisplay = (weight: number): number => {
    const roundedNumber = Math.floor(weight * 100) / 100;

    if (roundedNumber === weight) {
        return weight;
    }

    return roundedNumber;
};

const mergeReplacedIngredient = (
    existing: ProductFormProductIngredient,
    selected: ProductFormProductIngredient,
    ratiosByCompositeId?: EcobalyseIngredientMassRatiosMap,
): ProductFormProductIngredient => {
    const usedQuantity = existing.usedQuantity;
    const quantity =
        existing.quantityOverridden ? existing.quantity
        : usedQuantity === null ? existing.quantity
        : Math.round(
                resolveEdibleMassG(
                    {
                        ...selected,
                        usedQuantity,
                        quantity: existing.quantity,
                    },
                    ratiosByCompositeId,
                ),
            );

    return {
        ...selected,
        "@id": existing["@id"],
        id: existing.id,
        quantity,
        usedQuantity,
        quantityOverridden: existing.quantityOverridden,
        origin: selected.origin ?? selected.ingredientReference?.defaultOrigin ?? existing.origin,
    };
};

export const Step4Recipe = (props: Step4RecipeProps) => {
    const {
        product,
        setProduct,
        disabled = false,
        errors = [],
        lineScores = {},
        transportLineScores = {},
        ingredientEcsBreakdown = {},
        resolvedTransports = [],
        scoreUnit = "Pts",
        onTransportMaterializationPendingChange,
    } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [isIngredientModalOpen, setIngredientModalOpen] = useState(false);
    const [editingIngredientIndex, setEditingIngredientIndex] = useState<number | null>(null);

    const openIngredientModalForAdd = () => {
        setEditingIngredientIndex(null);
        setIngredientModalOpen(true);
    };

    const openIngredientModalForEdit = (index: number) => {
        setEditingIngredientIndex(index);
        setIngredientModalOpen(true);
    };

    const closeIngredientModal = () => {
        setIngredientModalOpen(false);
        setEditingIngredientIndex(null);
    };

    const setProductIngredients = (productIngredients: ProductFormType["productIngredients"]) => {
        setProduct((currentProduct) => ({
            ...currentProduct,
            productIngredients,
        }));
    };

    const updateIngredientAtIndex = (index: number, patch: Partial<ProductFormType["productIngredients"][number]>) => {
        setProductIngredients(
            product.productIngredients.map((ingredient, currentIndex) => {
                if (currentIndex !== index) {
                    return ingredient;
                }

                const nextIngredient = {
                    ...ingredient,
                    ...patch,
                };

                if (patch.usedQuantity !== undefined && !nextIngredient.quantityOverridden) {
                    const usedQuantityG = patch.usedQuantity ?? 0;
                    nextIngredient.quantity = Math.round(
                        computeEdibleMassG(
                            usedQuantityG,
                            resolveIngredientMassRatios(nextIngredient, ratiosByCompositeId),
                        ),
                    );
                }

                return nextIngredient;
            }),
        );
    };

    const removeIngredientAtIndex = (index: number) => {
        setProductIngredients(product.productIngredients.filter((_, currentIndex) => currentIndex !== index));
    };

    const ratiosByCompositeId = useIngredientMassRatios(product.productIngredients);

    useHydrateIngredientOrigins(product.productIngredients, setProduct);

    const { totalWeight, difference, hasWeightMismatch, isConsistent } = getRecipeWeightValidation(product);
    const netWeight = product.netWeight;
    const weightToDisplay = formatWeightForDisplay(totalWeight);

    return (
        <Box
            sx={{
                padding: theme.spacing(3),
            }}
        >
            <IngredientsSearchModalV2
                productIngredients={product.productIngredients}
                editingIndex={editingIngredientIndex}
                open={isIngredientModalOpen}
                onClose={closeIngredientModal}
                onSelect={(ingredient) => {
                    const ingredientWithOrigin = resolveSelectedIngredientOrigin(ingredient);
                    const ingredientWithEdibleMass = {
                        ...ingredientWithOrigin,
                        quantity:
                            ingredientWithOrigin.usedQuantity === null ?
                                ingredientWithOrigin.quantity
                            :   Math.round(
                                    computeEdibleMassG(
                                        ingredientWithOrigin.usedQuantity,
                                        resolveIngredientMassRatios(ingredientWithOrigin, ratiosByCompositeId),
                                    ),
                                ),
                    };

                    if (editingIngredientIndex !== null) {
                        setProductIngredients(
                            product.productIngredients.map((existing, currentIndex) =>
                                currentIndex === editingIngredientIndex ?
                                    mergeReplacedIngredient(existing, ingredientWithEdibleMass, ratiosByCompositeId)
                                :   existing,
                            ),
                        );
                        return;
                    }

                    setProductIngredients([...product.productIngredients, ingredientWithEdibleMass]);
                }}
            />

            {product.productIngredients.length === 0 ?
                <AddItemField
                    description={t("product.v2.recipe.empty_state", "Add a first ingredient to your product.")}
                    actionLabel={t("product.v2.recipe.add_ingredient", "Add an ingredient")}
                    onAction={openIngredientModalForAdd}
                />
            :   <Stack data-testid="recipe-filled-content" gap={theme.spacing(3)}>
                    <Stack data-testid="recipe-ingredient-list" gap={theme.spacing(2)}>
                        {product.productIngredients.map((productIngredient, index) => {
                            const draftIngredientKey = getProductIngredientTransportKey(productIngredient);
                            const lineScoreKey = getIngredientLineScoreKey(productIngredient);
                            const persistedTransportKey = getIngredientTransportLineScoreKey(productIngredient.id);
                            const transportLineScore =
                                (persistedTransportKey !== null ? transportLineScores[persistedTransportKey] : null) ??
                                (draftIngredientKey !== null ? transportLineScores[draftIngredientKey] : null) ??
                                null;
                            const resolvedTransport = resolvedTransports.find(
                                (transport) =>
                                    transport.lineKey === draftIngredientKey &&
                                    transport.stage === "ingredient_to_processing_site",
                            );
                            return (
                                <IngredientCardV2
                                    key={productIngredient["@id"] ?? `${lineScoreKey ?? "ingredient"}-${index}`}
                                    product={product}
                                    setProduct={setProduct}
                                    disabled={disabled}
                                    productIngredient={productIngredient}
                                    ingredientError={errors.find(
                                        (error) => error.field === `productIngredients.${index}.ingredient`,
                                    )}
                                    quantityError={errors.some(
                                        (error) => error.field === `productIngredients.${index}.quantity`,
                                    )}
                                    ecsBreakdown={
                                        lineScoreKey !== null ? ingredientEcsBreakdown[lineScoreKey] : undefined
                                    }
                                    ingredientLineScore={
                                        lineScoreKey !== null ? (lineScores[lineScoreKey] ?? null) : null
                                    }
                                    transportLineScore={transportLineScore}
                                    resolvedTransport={resolvedTransport}
                                    scoreUnit={scoreUnit}
                                    onEdit={() => openIngredientModalForEdit(index)}
                                    onRemove={() => removeIngredientAtIndex(index)}
                                    onUsedQuantityChange={(newValue) => {
                                        updateIngredientAtIndex(index, {
                                            usedQuantity: newValue === "" ? null : Number(newValue),
                                        });
                                    }}
                                    onQuantityChange={(newValue) => {
                                        updateIngredientAtIndex(index, {
                                            quantity:
                                                newValue === "" ? null
                                                : Number.isFinite(Number(newValue)) ? Number(newValue)
                                                : null,
                                            quantityOverridden: true,
                                        });
                                    }}
                                    onOriginChange={(origin) => {
                                        updateIngredientAtIndex(index, { origin });
                                    }}
                                    onTransportMaterializationPendingChange={onTransportMaterializationPendingChange}
                                />
                            );
                        })}
                    </Stack>

                    {hasWeightMismatch && (
                        <Stack
                            role="status"
                            direction="row"
                            alignItems="center"
                            gap={theme.spacing(1)}
                            sx={{ color: theme.custom.red[600] }}
                        >
                            <FoodsIcon icon="warning" size={1} />
                            <Typography
                                variant="small-medium"
                                sx={{
                                    color: theme.custom.red[600],
                                }}
                            >
                                {t(
                                    "product.v2.recipe.net_weight_mismatch",
                                    "Total weight after processing: {{used}}g — net weight: {{net}}g — difference: {{diff}}g",
                                    {
                                        used: weightToDisplay,
                                        net: netWeight,
                                        diff: formatWeightForDisplay(difference),
                                    },
                                )}
                            </Typography>
                        </Stack>
                    )}

                    {isConsistent && (
                        <Stack
                            role="status"
                            direction="row"
                            alignItems="center"
                            gap={theme.spacing(1)}
                            sx={{ color: theme.custom.green[600] }}
                        >
                            <FoodsIcon icon="check" size={1} />
                            <Typography variant="small-medium" sx={{ color: theme.custom.green[600] }}>
                                {t(
                                    "product.v2.recipe.net_weight_consistent",
                                    "Total weight after processing: {{total}}g",
                                    { total: weightToDisplay },
                                )}
                            </Typography>
                        </Stack>
                    )}

                    <AddItemField
                        actionLabel={t("product.v2.recipe.add_ingredient", "Add an ingredient")}
                        onAction={openIngredientModalForAdd}
                    />
                </Stack>
            }
        </Box>
    );
};
